import { SolDialogState } from '../abstract-dialog-component.directive';
import { SolButton } from '../../../models/core/sol-button';
import { SolDialog } from '../../../models/core/sol-dialog';
import { SolImage } from '../../../models/core/sol-image';
import { SolLoader } from '../../../models/core/sol-loader';
import { ObjectTools } from '../../../tools/object-tools';

/** Which of the image's two sources the viewer is showing, and whether both have failed. */
export type SolImageDialogSource = 'primary' | 'fallback' | 'failed';

export class SolImageDialogState implements SolDialogState {
  private constructor(
    public readonly dialog: SolDialog,
    public readonly image: SolImage,
    public readonly title: string,
    public readonly actionButton: SolButton | undefined,
    public readonly loading: boolean,
    public readonly error: boolean,
    public readonly source: SolImageDialogSource,
    public readonly displaySrc: string,
    public readonly hasError: boolean,
    public readonly hasToolbar: boolean,
    public readonly loader: SolLoader,
  ) {}

  public static of(): SolImageDialogState {
    return new SolImageDialogState(
      SolDialog.ofChromeless(),
      SolImage.empty(),
      '',
      undefined,
      false,
      false,
      'primary',
      '',
      false,
      false,
      SolLoader.ofBlocks(1, 1, '256'),
    );
  }

  public withDialog(dialog: SolDialog): this {
    return this.copy({ dialog });
  }

  public withImage(image: SolImage): this {
    return this.copy({ image, source: 'primary' }).recalculateDisplaySrc().recalculateHasError();
  }

  public withTitle(title: string): this {
    return this.copy({ title }).recalculateHasToolbar();
  }

  public withActionButton(actionButton: SolButton | undefined): this {
    return this.copy({ actionButton }).recalculateHasToolbar();
  }

  public withLoading(loading: boolean): this {
    return this.copy({ loading });
  }

  public withError(error: boolean): this {
    return this.copy({ error }).recalculateHasError();
  }

  /** The shown source failed to load: drop to the thumbnail once, and to the error state when that fails too. */
  public withLoadFailure(): this {
    const canFallBack = this.source === 'primary' && this.image.src !== '' && this.displaySrc !== this.image.src;
    return this.copy({ source: canFallBack ? 'fallback' : 'failed' })
      .recalculateDisplaySrc()
      .recalculateHasError();
  }

  private recalculateDisplaySrc(): this {
    const displaySrc = this.source === 'fallback' ? this.image.src : (this.image.fullScreenSrc ?? this.image.src);
    return this.copy({ displaySrc });
  }

  /** With no title and no action there is nothing to put in the bar, and a lightbox shows no empty chrome. */
  private recalculateHasToolbar(): this {
    return this.copy({ hasToolbar: this.title !== '' || this.actionButton !== undefined });
  }

  private recalculateHasError(): this {
    return this.copy({ hasError: this.error || this.source === 'failed' });
  }

  private copy(props: Partial<SolImageDialogState>): this {
    return ObjectTools.copy<SolImageDialogState>(this, props) as this;
  }
}
